import { UkDateInput } from "@/components/UkDateInput";

import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

const inp = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => (d === "0000-01-01" ? "Start" : new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }));

export function PayRatesEditor({ staffId, onSaved }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ hourly_rate: "", mileage_rate: "", effective_from: today(), note: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/staff/${staffId}/pay-rates`).then((r) => {
    setData(r.data);
    setF((x) => ({ ...x, hourly_rate: x.hourly_rate === "" ? r.data.current.hourly_rate : x.hourly_rate, mileage_rate: x.mileage_rate === "" ? r.data.current.mileage_rate : x.mileage_rate }));
  }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [staffId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/staff/${staffId}/pay-rates`, { ...f, hourly_rate: parseFloat(f.hourly_rate) || 0, mileage_rate: parseFloat(f.mileage_rate) || 0 });
      toast.success(`New rate saved from ${fmtDate(f.effective_from)}`); setF({ ...f, note: "" }); load(); onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const del = async (r) => {
    if (!window.confirm(`Remove the rate from ${fmtDate(r.effective_from)}?`)) return;
    try { await api.delete(`/staff/${staffId}/pay-rates/${r.id}`); toast.success("Rate removed"); load(); onSaved && onSaved(); } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <p className="text-sm text-ink-muted">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="pay-rates-editor">
      <div className="flex flex-wrap gap-3">
        <div className="rounded-lg bg-sage-light px-4 py-2"><p className="text-[11px] font-semibold uppercase tracking-wider text-sage-dark">Current hourly</p><p className="font-heading text-lg font-bold text-sage-dark" data-testid="current-hourly-rate">£{data.current.hourly_rate.toFixed(2)}</p></div>
        <div className="rounded-lg bg-secondary px-4 py-2"><p className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">Current mileage</p><p className="font-heading text-lg font-bold text-ink" data-testid="current-mileage-rate">£{data.current.mileage_rate.toFixed(2)}/mi</p></div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <label className="text-xs font-medium text-ink-soft">Hourly rate (£)<input type="number" step="0.01" min="0" className={inp} value={f.hourly_rate} onChange={(e) => setF({ ...f, hourly_rate: e.target.value })} data-testid="new-hourly-rate-input" /></label>
        <label className="text-xs font-medium text-ink-soft">Mileage (£/mile)<input type="number" step="0.01" min="0" className={inp} value={f.mileage_rate} onChange={(e) => setF({ ...f, mileage_rate: e.target.value })} data-testid="new-mileage-rate-input" /></label>
        <label className="col-span-2 text-xs font-medium text-ink-soft">Applies from<UkDateInput value={f.effective_from} onChange={(v) => setF({ ...f, effective_from: v })} testid="rate-effective-input" /></label>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input className={inp} placeholder="Note (optional), e.g. Annual pay rise" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="rate-note-input" />
        <button type="button" onClick={save} disabled={busy} data-testid="save-pay-rate-button" className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-60"><Plus size={15} /> {busy ? "Saving…" : "Save new rate"}</button>
      </div>
      <p className="text-xs text-ink-muted">Visits before the "applies from" date keep the earlier rate when payroll is run.</p>
      {data.history.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border" data-testid="pay-rate-history">
          <table className="w-full text-sm">
            <thead className="bg-ivory text-left text-[11px] font-semibold uppercase tracking-wider text-ink-muted"><tr><th className="px-3 py-2">From</th><th className="px-3 py-2">Hourly</th><th className="px-3 py-2">Mileage</th><th className="px-3 py-2">Note</th><th /></tr></thead>
            <tbody className="divide-y divide-border">
              {data.history.map((r) => (
                <tr key={r.id} data-testid={`rate-row-${r.id}`}>
                  <td className="px-3 py-2 text-ink">{fmtDate(r.effective_from)}</td>
                  <td className="px-3 py-2 text-ink">£{Number(r.hourly_rate).toFixed(2)}</td>
                  <td className="px-3 py-2 text-ink-soft">£{Number(r.mileage_rate).toFixed(2)}</td>
                  <td className="px-3 py-2 text-xs text-ink-muted">{r.note}{r.set_by ? ` · ${r.set_by}` : ""}</td>
                  <td className="px-3 py-2 text-right">{r.effective_from !== "0000-01-01" && <button type="button" onClick={() => del(r)} data-testid={`delete-rate-${r.id}`} className="text-ink-muted hover:text-coral-dark"><Trash2 size={14} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
